import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { noop } from '../lib/serviceHelpers';
import { getCookie } from '../lib/cookies';

// Onboarding Support board. One payload: shared intake + my cadence runs
// (steps, currentDay, detections) + the active cadence definition. Polling is
// the fallback; the SSE stream below invalidates on real events.
export const useFetchBoard = (params = {}, opts = {}) => {
  return useQuery(
    ['onboarding-board', params],
    async () => {
      const res = await axios.get(`/onboarding-board`, { params });
      return res.data;
    },
    {
      refetchInterval: 30000,
      // Keep polling while the window is UNFOCUSED too — the board lives on
      // second monitors; a paused poll made new leads appear with their
      // intake clock already burned (Nic 2026-08-08).
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      staleTime: 5000,
      ...opts,
    },
  );
};

// Booking attribution (2026-08-31): who TRULY booked. The server enforces
// scope (OSS callers are pinned to themselves; leadership sees all / any
// ?userId=), so this hook is safe to mount on both the board and the
// Cadence tab.
export const useBookingAttribution = (params = {}, opts = {}) =>
  useQuery(
    ['booking-attribution', params],
    async () => {
      const res = await axios.get(`/onboarding-board/bookings-attribution`, {
        params,
      });
      return res.data;
    },
    {
      keepPreviousData: true,
      staleTime: 30000,
      refetchOnWindowFocus: true,
      ...opts,
    },
  );

export const useTakeLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId }) => {
      const res = await axios.post(`/onboarding-board/leads/${leadId}/take`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Reversible Push (Nic 2026-08-15): during the last working hour, move an
// untaken intake lead's clock start to the next working open; Undo gives it
// back to whoever pushed it, its clock starting now (QA 2026-10-26). Both are
// refused outside the window (409).
export const usePushLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId }) => {
      const res = await axios.post(`/onboarding-board/intake/${leadId}/push`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Start a waiting new lead early (Nic 2026-10-25): owner only, while the
// board is open. Its clock restarts from now.
export const useStartRunNow = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/start-now`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUnpushLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId }) => {
      const res = await axios.post(`/onboarding-board/intake/${leadId}/unpush`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Call steps require outcome: reached | no_answer | left_vm.
export const useCompleteStep = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceStepId, ...payload }) => {
      const res = await axios.post(
        `/onboarding-board/steps/${cadenceStepId}/complete`,
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ---------------------------------------------------------------------------
// Event Leads parking lot (board-header page; work-gated, board family).
// ---------------------------------------------------------------------------
export const useFetchEventLeads = (params = {}, opts = {}) => {
  return useQuery(
    ['event-leads', params],
    async () => {
      const res = await axios.get('/event-leads', { params });
      return res.data;
    },
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      staleTime: 5000,
      ...opts,
    },
  );
};

export const useMarkEventLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, mark }) => {
      const res = await axios.post(`/event-leads/${leadId}/mark`, { mark });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Claim an event lead into the 10-day cadence: the exact backlog-claim write
// (assigned to the clicker, clock starts, claim is the arrival).
export const useClaimEventLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId }) => {
      const res = await axios.post(`/event-leads/${leadId}/claim`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Fourth call outcome: "didn't go through" — records the aborted attempt
// (audit + reconcile exclusion) and leaves the step actionable for retry.
export const useDiscardCallAttempt = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceStepId, ...payload }) => {
      const res = await axios.post(
        `/onboarding-board/steps/${cadenceStepId}/discard-call`,
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// "Check back in" from lunch: the signed-in specialist ends today's lunch
// early; the board payload's lunch instants reflect it on the next fetch.
export const useLunchCheckIn = (callback, opts = {}) => {
  return useMutation(
    async () => {
      const res = await axios.post(`/onboarding-board/lunch/check-in`, {});
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Step away for a short break (Nic 2026-10-05): { reason }. Ends by itself
// after 15 minutes; "I'm back" is useLunchCheckIn.
export const useGoAway = (callback, opts = {}) =>
  useMutation(
    // { reason, note } (note only for Other, 40 characters at most).
    async ({ reason, note = null }) => {
      const res = await axios.post(`/onboarding-board/away`, {
        reason,
        ...(note ? { note } : {}),
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );

// Reset a day to the DEFAULT plan (deletes its saved rows; the calendar
// cell reads DEFAULT again).
export const useResetScheduleDay = (callback, opts = {}) => {
  return useMutation(
    async ({ date }) => {
      const res = await axios.delete(`/onboarding-board/schedule/${date}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Jorge sets a specialist's STANDING lunch (Lunches card). Pair or nothing.
export const useSetUserDefaultLunch = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, lunchStartLocal, lunchEndLocal }) => {
      const res = await axios.put(
        `/onboarding-board/users/${userId}/default-lunch`,
        { lunchStartLocal, lunchEndLocal },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Jorge sets a specialist's STANDING working hours (Hours card, 2026-09-01).
// Pair or nothing; blank pair = follow the org working hours.
export const useSetUserDefaultHours = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, workStartLocal, workEndLocal }) => {
      const res = await axios.put(
        `/onboarding-board/users/${userId}/default-hours`,
        { workStartLocal, workEndLocal },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// RC status chip: which number would this browser's RC login text from?
// { fromNumber } — null when signed in but no SMS-enabled number.
export const useMyRcSmsNumber = (opts = {}, apiBase = '/onboarding-board') => {
  return useMutation(async ({ rcAccessToken }) => {
    const res = await axios.post(`${apiBase}/rc/sms-number`, {
      rcAccessToken,
    });
    return res.data;
  }, opts);
};

// Real sends (Phase 3): the board texts/emails the lead itself. A failed
// send leaves the step pending; the server's error message is user-facing.
export const useSendStepSms = (callback, opts = {}) => {
  return useMutation(
    // rcAccessToken: the signed-in person's own RingCentral OAuth token
    // (the dialer login) — texts go out AS them, from their own line.
    async ({ cadenceStepId, text, rcAccessToken }) => {
      const res = await axios.post(
        `/onboarding-board/steps/${cadenceStepId}/send-sms`,
        { text, rcAccessToken },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSendStepEmail = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceStepId, subject, text }) => {
      const res = await axios.post(
        `/onboarding-board/steps/${cadenceStepId}/send-email`,
        { subject, text },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Outbound reconcile: off-page RC activity for a run (fetched when the
// slide-over opens; the server degrades to rcUnavailable on RC trouble).
// Keyed OUTSIDE the ['onboarding-board'] prefix on purpose: the board
// stream invalidates that prefix on every frame (400ms debounce), which
// made each board repaint fire a LIVE RingCentral round-trip and defeated
// the 30s staleTime. Call actions that should refresh this invalidate
// ['rc-activity'] explicitly.
export const useFetchRunRcActivity = (cadenceRunId, opts = {}) => {
  return useQuery(
    ['rc-activity', cadenceRunId],
    async () => {
      const res = await axios.get(
        `/onboarding-board/runs/${cadenceRunId}/rc-activity`,
      );
      return res.data;
    },
    { enabled: !!cadenceRunId, staleTime: 30000, ...opts },
  );
};

// Adopt an off-page RC record onto a pending step (server re-verifies the
// record; its timestamp becomes the SLA moment).
export const useAdoptOutbound = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceStepId, ...payload }) => {
      const res = await axios.post(
        `/onboarding-board/steps/${cadenceStepId}/adopt-outbound`,
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Dismiss inbound-detection callouts (all of the lead's unacknowledged
// detections when leadEventIds is omitted).
export const useAckDetections = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, leadEventIds }) => {
      const res = await axios.post(
        `/onboarding-board/leads/${leadId}/detections/ack`,
        leadEventIds ? { leadEventIds } : {},
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// via: call | sms | email | callback (+ optional leadOutreachId to link an
// already-logged touch instead of logging a new inbound one).
export const useMarkContactMade = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, ...payload }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/contact-made`,
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// "Not yet" on the post-call prompt: contact happened but the cadence keeps
// going. The observation is stamped so Confirm contact made can backdate.
export const useObserveContact = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, leadOutreachId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/observe-contact`,
        leadOutreachId ? { leadOutreachId } : {},
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Per-user board display preferences (tile style, ...). Saved server-side so
// choices follow the specialist across devices.
// The Sales Rep's own Calendly (2026-09-23): meetings for their leads book
// on it. The server checks it against the company Calendly before saving.
export const useSaveBoardCalendlyLink = (callback, opts = {}, apiBase = '/onboarding-board') => {
  return useMutation(
    async ({ calendlyUrl }) => {
      const res = await axios.put(`${apiBase}/my-calendly-link`, {
        calendlyUrl,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// The call workspace's talk track (managers).
export const useSaveCallGuide = (callback, opts = {}) => {
  return useMutation(
    async ({ sections, faqs }) => {
      const res = await axios.put('/onboarding-board/call-guide', {
        sections,
        faqs,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSaveBoardPrefs = (callback, opts = {}) => {
  return useMutation(
    async (patch) => {
      const res = await axios.patch('/onboarding-board/prefs', patch);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ---- Signing Up's desk (Nic 2026-10-21) ----
// The same Today list as the Onboarding Workspace's Clients tab: their own
// to-dos (about a lead or nobody), plus the suggestions they checked off.
const SALES_TODOS_KEY = ['onboarding-board-todos'];
export const useSalesTodos = (enabled = true) =>
  useQuery(
    SALES_TODOS_KEY,
    async () => (await axios.get('/onboarding-board/todos')).data,
    { enabled: !!enabled, refetchOnWindowFocus: true },
  );
const useSalesTodoMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => queryClient.invalidateQueries(SALES_TODOS_KEY),
  });
};
export const useAddSalesTodo = () =>
  useSalesTodoMutation(
    async (body) => (await axios.post('/onboarding-board/todos', body)).data,
  );
export const useUpdateSalesTodo = () =>
  useSalesTodoMutation(
    async ({ todoId, ...body }) =>
      (await axios.patch(`/onboarding-board/todos/${todoId}`, body)).data,
  );
export const useDeleteSalesTodo = () =>
  useSalesTodoMutation(
    async ({ todoId }) =>
      (await axios.delete(`/onboarding-board/todos/${todoId}`)).data,
  );

// Pin a lead to their quick list. The star flips at once (every cached
// board's prefs.suPins); the server's answer settles it.
export const useSetSalesPinned = () => {
  const queryClient = useQueryClient();
  const setPins = (fn) =>
    queryClient.setQueriesData(['onboarding-board'], (data) =>
      data && data.prefs
        ? { ...data, prefs: { ...data.prefs, suPins: fn(data.prefs.suPins || []) } }
        : data,
    );
  return useMutation(
    async ({ leadId, pinned }) =>
      (await axios.put(`/onboarding-board/leads/${leadId}/pin`, { pinned })).data,
    {
      onMutate: ({ leadId, pinned }) => {
        setPins((pins) => [...pins.filter((x) => x !== leadId), ...(pinned ? [leadId] : [])]);
      },
      onSuccess: (res) => setPins(() => res?.pinned || []),
      onError: () => queryClient.invalidateQueries(['onboarding-board']),
    },
  );
};

// ---------------------------------------------------------------------------
// Ownership & Schedule round
// ---------------------------------------------------------------------------

// Manager slideout data: roster per day, work calendar, and days off.
export const useFetchBoardSchedule = (params = {}, opts = {}) => {
  return useQuery(
    ['onboarding-board-schedule', params],
    async () => {
      const res = await axios.get('/onboarding-board/schedule', { params });
      return res.data;
    },
    { staleTime: 15000, refetchOnWindowFocus: true, ...opts },
  );
};

// Replace a day's roster (array order = alternation order).
export const useSaveScheduleDay = (callback, opts = {}) => {
  return useMutation(
    async ({ date, users }) => {
      const res = await axios.put(`/onboarding-board/schedule/${date}`, {
        users,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Org work calendar: working hours + working weekdays (manager-only; the
// cadence engine follows these).
export const useSaveWorkCalendar = (callback, opts = {}) => {
  return useMutation(
    async (patch) => {
      const res = await axios.put('/onboarding-board/work-calendar', patch);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Mark/unmark a specific date as a day off (holiday/closure).
export const useSaveDayOff = (callback, opts = {}) => {
  return useMutation(
    async ({ date, off, label }) => {
      const res = await axios.put(`/onboarding-board/days-off/${date}`, {
        off,
        label,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Manager reassignment: a live run, or an un-taken intake lead.
export const useReassignRun = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, toUserId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/reassign`,
        { toUserId },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useReassignIntake = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, toUserId }) => {
      const res = await axios.post(
        `/onboarding-board/leads/${leadId}/reassign-intake`,
        { toUserId },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Call-in-progress broadcast (fire-and-forget from the dialer).
export const useCallStarted = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, cadenceStepId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/call-started`,
        { cadenceStepId },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useCallEnded = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, failed = false }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/call-ended`,
        failed ? { failed: true } : {},
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// The per-lead working notepad. Explicit save only — never wire this to an
// autosave effect.
export const useSaveLeadWorkNotes = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, notes }) => {
      const res = await axios.patch(
        `/onboarding-board/leads/${leadId}/work-notes`,
        { notes },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Add or correct the lead's email from the slide-over. Board-scoped route,
// not PUT /leads/:leadId — the support specialist role lacks leads:manage.
export const useSaveLeadEmail = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, email }) => {
      const res = await axios.patch(`/onboarding-board/leads/${leadId}/email`, {
        email,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// outcome: booked | transferred | nurture.
export const useSetRunOutcome = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, ...payload }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/outcome`,
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// "Keep working" on a STOP-parked run: clears the «STOP» · Confirm drop
// chip; the lead stays in Contact Made.
export const useDismissStop = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/dismiss-stop`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Confirm a DETECTED Calendly booking: ends the run booked; from a still-
// active run the server stamps contact made first (via 'calendly', at the
// booking's created time) so contactMadeAt never rides false.
export const useConfirmBooking = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, calendlyBookingId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/confirm-booking`,
        { calendlyBookingId },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Contact Made "Call them now": records the follow-up call as an outreach
// touch (no step, no SLA).
export const useLogFollowupCall = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, rcSessionId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/followup-call`,
        { rcSessionId },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Ad-hoc call to a TAKEN lead outside the checklist (2026-08-13): counts
// toward nothing; the server's lead_events row is the per-lead usage counter.
export const useLogOutsideCadenceCall = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceRunId, rcSessionId }) => {
      const res = await axios.post(
        `/onboarding-board/runs/${cadenceRunId}/outside-cadence-call`,
        { rcSessionId },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ---------------------------------------------------------------------------
// Cadence editor (Phase 4) — manager version CRUD + publish, and the
// per-specialist call profile ([calendly link]/[bio] tokens).
// ---------------------------------------------------------------------------

export const useFetchCadenceVersions = (opts = {}) => {
  return useQuery(
    ['cadence-versions'],
    async () => {
      const res = await axios.get('/onboarding-board/cadence/versions');
      return res.data;
    },
    { staleTime: 15000, refetchOnWindowFocus: true, ...opts },
  );
};

export const useFetchCadenceVersion = (cadenceVersionId, opts = {}) => {
  return useQuery(
    ['cadence-versions', cadenceVersionId],
    async () => {
      const res = await axios.get(
        `/onboarding-board/cadence/versions/${cadenceVersionId}`,
      );
      return res.data;
    },
    { enabled: !!cadenceVersionId, staleTime: 15000, ...opts },
  );
};

export const useCreateCadenceDraft = (callback, opts = {}) => {
  return useMutation(
    async (payload = {}) => {
      const res = await axios.post(
        '/onboarding-board/cadence/versions',
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSaveCadenceDraft = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceVersionId, ...payload }) => {
      const res = await axios.put(
        `/onboarding-board/cadence/versions/${cadenceVersionId}`,
        payload,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteCadenceDraft = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceVersionId }) => {
      const res = await axios.delete(
        `/onboarding-board/cadence/versions/${cadenceVersionId}`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Approval gate (2026-08-15): the manager submits and can pull it back;
// publishing belongs to whoever holds onboarding_cadence:approve.
export const useSubmitCadenceForApproval = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceVersionId }) => {
      const res = await axios.post(
        `/onboarding-board/cadence/versions/${cadenceVersionId}/submit`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useWithdrawCadenceSubmission = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceVersionId }) => {
      const res = await axios.post(
        `/onboarding-board/cadence/versions/${cadenceVersionId}/withdraw`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const usePublishCadenceVersion = (callback, opts = {}) => {
  return useMutation(
    async ({ cadenceVersionId }) => {
      const res = await axios.post(
        `/onboarding-board/cadence/versions/${cadenceVersionId}/publish`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// The messages a cadence step can send (written on the Messages page,
// 2026-10-22). Live, off the versioned cadence.
export const useFetchCadenceScripts = (opts = {}) => {
  return useQuery(
    ['cadence-scripts'],
    async () => {
      const res = await axios.get('/onboarding-board/cadence/scripts');
      return res.data;
    },
    { staleTime: 15000, refetchOnWindowFocus: true, ...opts },
  );
};

export const useFetchCallProfile = (opts = {}) => {
  return useQuery(
    ['call-profile'],
    async () => {
      const res = await axios.get('/onboarding-board/call-profile');
      return res.data;
    },
    { staleTime: 30000, ...opts },
  );
};

export const useSaveCallProfile = (callback, opts = {}) => {
  return useMutation(
    async (patch) => {
      const res = await axios.patch('/onboarding-board/call-profile', patch);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Test Audio: server-side vet of the number BEFORE the webphone dials it.
// Lead/client numbers come back 409 with the explanation copy.
export const useCheckTestCallNumber = (callback, opts = {}) => {
  return useMutation(
    async ({ phone }) => {
      const res = await axios.post('/onboarding-board/test-call/check', {
        phone,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// ---------------------------------------------------------------------------
// Real-time: fetch-based SSE reader (EventSource can't send the Bearer
// header). Any board hint invalidates the board query (debounced); the socket
// reconnects with capped backoff — nodemon restarts and network blips are
// routine, not errors.
// ---------------------------------------------------------------------------

const STREAM_DEBOUNCE_MS = 400;

/**
 * @param enabled
 * @param onEvent optional: called with each parsed board hint
 *   ({ type, leadId, ... }). Refetching stays debounced and unconditional as
 *   before; this is a side channel for reacting to a SPECIFIC event, which
 *   used to be impossible because the payload was thrown away.
 */
export const useBoardStream = (enabled = true, onEvent = null) => {
  const queryClient = useQueryClient();
  const debounceRef = useRef(null);
  // Held in a ref so an inline callback (a new identity every render) does
  // not tear down and reconnect the stream on every parent render.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let controller = null;
    let retryDelay = 1000;

    const invalidate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries(['onboarding-board']);
        // Board events move metrics too (takes, completions, arrivals) —
        // an open metrics page rides the same push.
        queryClient.invalidateQueries(['onboarding-metrics']);
        queryClient.invalidateQueries(['onboarding-board-schedule']);
        // A booking attaching to a watched lead clears its Follow-ups row;
        // no-op unless the panel's query is mounted.
        queryClient.invalidateQueries(['onboarding-followups']);
        // An open signup flyover flips the moment the veteran registers
        // or signs (no-op unless it is mounted).
        queryClient.invalidateQueries(['lead-signup']);
        queryClient.invalidateQueries(['onboarding-potential']);
        queryClient.invalidateQueries(['onboarding-meetings']);
        queryClient.invalidateQueries(['onboarding-journey']);
        queryClient.invalidateQueries(['onboarding-direct-signups']);
        // A signup ends a run (run_ended): the scoreboard's chip and card.
        queryClient.invalidateQueries(['onboarding-scoreboard']);
      }, STREAM_DEBOUNCE_MS);
    };

    const connect = async () => {
      while (!cancelled) {
        controller = new AbortController();
        try {
          const token = getCookie('token');
          const base = axios.defaults.baseURL || '';
          const response = await fetch(`${base}/onboarding-board/stream`, {
            headers: {
              Accept: 'text/event-stream',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            signal: controller.signal,
          });
          if (!response.ok || !response.body) {
            throw new Error(`stream ${response.status}`);
          }
          retryDelay = 1000; // healthy connection resets the backoff

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          for (;;) {
            const { done, value } = await reader.read();
            if (done || cancelled) break;
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split('\n\n');
            buffer = frames.pop();
            frames.forEach((frame) => {
              const line = frame
                .split('\n')
                .find((l) => l.startsWith('data: '));
              if (!line) return;
              invalidate();
              if (!onEventRef.current) return;
              try {
                onEventRef.current(JSON.parse(line.slice(6)));
              } catch {
                // A malformed frame (or a heartbeat that isn't JSON) must
                // never kill the stream — the refetch above already happened.
              }
            });
          }
        } catch (err) {
          if (cancelled) break;
        }
        if (cancelled) break;
        // Reconnect with capped backoff.
        const delay = retryDelay;
        await new Promise((resolve) => setTimeout(resolve, delay));
        retryDelay = Math.min(retryDelay * 2, 15000);
      }
    };

    connect();
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      controller?.abort();
    };
  }, [enabled, queryClient]);
};

// ---------------------------------------------------------------------------
// Signup on the call (Nic 2026-09-22): the Sales Rep's lead-scoped twin of the
// Signups page API. The flyover polls this while it waits for the veteran to
// register (the board SSE also invalidates it on lead_updated).
// ---------------------------------------------------------------------------
export const useFetchLeadSignup = (leadId, opts = {}) =>
  useQuery(
    ['lead-signup', leadId],
    async () => {
      const res = await axios.get(`/onboarding-board/leads/${leadId}/signup`);
      return res.data;
    },
    { enabled: !!leadId, refetchInterval: 5000, ...opts },
  );

// The hot seat card's lights (Nic 2026-10-29): who is free, and the rep's
// live warm-transfer consult as the specialist's line reports it. Polled
// only while the card is up; faster while a consult rings.
export const useHotSeatAvailability = (leadId, { enabled = true, consulting = false } = {}) =>
  useQuery(
    ['hot-seat-availability', leadId],
    async () => {
      const res = await axios.get(`/onboarding-board/leads/${leadId}/hot-seat-availability`);
      return res.data;
    },
    { enabled: !!leadId && enabled, refetchInterval: consulting ? 1500 : 3000, keepPreviousData: true },
  );

export const sendLeadSignupLink = async (
  leadId,
  { channel, rcAccessToken },
) => {
  const res = await axios.post(
    `/onboarding-board/leads/${leadId}/signup/send-link`,
    { channel, rcAccessToken },
  );
  return res.data;
};

export const claimLeadSignup = async (leadId) => {
  const res = await axios.post(
    `/onboarding-board/leads/${leadId}/signup/claim`,
  );
  return res.data;
};

// The `api` object the shared FactsSection / AgreementSection take.
export const boardSignupApiFor = (leadId) => {
  const post = async (path, body) => {
    const res = await axios.post(
      `/onboarding-board/leads/${leadId}/signup/${path}`,
      body,
    );
    return res.data;
  };
  return {
    confirmFacts: (facts) => post('confirm-facts', facts),
    sendAgreement: ({ regenerate = false } = {}) =>
      post('send-service-agreement', { regenerate }),
    voidAgreement: () => post('void-agreement'),
    signLink: () => post('sign-link'),
    beginCorrection: (why) => post('begin-correction', why),
    cancelCorrection: () => post('cancel-correction'),
  };
};

// ---------------------------------------------------------------------------
// Potential Clients view (Nic 2026-09-22): the rep's follow-up tasks + the
// direct-signups bucket. The board SSE invalidates these on every event.
// ---------------------------------------------------------------------------
export const useFetchPotentialClients = (opts = {}) =>
  useQuery(
    ['onboarding-potential'],
    async () => {
      const res = await axios.get('/onboarding-board/potential');
      return res.data;
    },
    { refetchInterval: 60000, staleTime: 5000, ...opts },
  );

// The meetings calendar (2026-09-24). from/to are ISO instants; scope 'all'
// is honored for managers only. Refetches each minute so the alert timing
// and the calendar stay current without the stream.
export const useFetchMeetings = (
  { from, to, scope, apiBase = '/onboarding-board' } = {},
  opts = {},
) =>
  useQuery(
    ['onboarding-meetings', apiBase, from || null, to || null, scope || 'mine'],
    async () => {
      const res = await axios.get(`${apiBase}/meetings`, {
        params: { from, to, ...(scope === 'all' ? { scope } : {}) },
      });
      return res.data;
    },
    {
      refetchInterval: 60000,
      staleTime: 15000,
      keepPreviousData: true,
      ...opts,
    },
  );

// ---- Stage cadence actions (2026-09-24) ----
// The lead journey (milestones + every call, text and email), newest first.
export const useFetchLeadJourney = (leadId, opts = {}) =>
  useQuery(
    ['onboarding-journey', leadId],
    async () => {
      const res = await axios.get(
        '/onboarding-board/leads/' + leadId + '/journey',
      );
      return res.data;
    },
    { enabled: !!leadId, staleTime: 10000, ...opts },
  );

// The prefilled nudge for a lead's current step: { text, optedOut, hasPhone }.
export const fetchNudgeDraft = async (leadId) => {
  const res = await axios.get(`/onboarding-board/leads/${leadId}/nudge-draft`);
  return res.data;
};

const refreshPotential = (queryClient) => {
  queryClient.invalidateQueries(['onboarding-board']);
  queryClient.invalidateQueries(['onboarding-meetings']);
  queryClient.invalidateQueries(['onboarding-journey']);
};

// Send the nudge from the rep's own line (rcAccessToken from the browser).
export const useSendNudge = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ leadId, text, rcAccessToken }) => {
      const res = await axios.post(`/onboarding-board/leads/${leadId}/nudge`, {
        text,
        rcAccessToken,
      });
      return res.data;
    },
    {
      ...opts,
      onSuccess: (...a) => {
        refreshPotential(queryClient);
        opts.onSuccess?.(...a);
      },
    },
  );
};

// The rep confirms the VA.gov account answer on the call:
// answer 'yes' | 'no' | 'unsure'.
export const useConfirmVaGov = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ leadId, answer }) => {
      const res = await axios.post(`/onboarding-board/leads/${leadId}/va-gov`, {
        answer,
      });
      return res.data;
    },
    {
      ...opts,
      onSuccess: (...a) => {
        refreshPotential(queryClient);
        // The signup's facts step carries it too (2026-10-05).
        queryClient.invalidateQueries(['lead-signup']);
        opts.onSuccess?.(...a);
      },
    },
  );
};

export const useFetchDirectSignups = (opts = {}) =>
  useQuery(
    ['onboarding-direct-signups'],
    async () => {
      const res = await axios.get('/onboarding-board/direct-signups');
      return res.data;
    },
    { staleTime: 5000, ...opts },
  );

export const useClaimDirectSignup = (callback = noop, opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ userId }) => {
      const res = await axios.post(
        `/onboarding-board/direct-signups/${userId}/claim`,
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['onboarding-direct-signups']);
        queryClient.invalidateQueries(['onboarding-potential']);
        queryClient.invalidateQueries(['onboarding-board']);
        callback(data);
      },
      ...opts,
    },
  );
};

// Hot seat at signing (Nic 2026-09-28): the signed client goes to the
// Onboarding Specialist the rep picked, connected or voicemail.
export const useRecordHotSeat = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    // Who it went to, nothing about how it went (Nic 2026-10-23):
    // RingCentral says answered or missed. warm = the specialist was on the
    // consult before the hand-over, so that one is connected. A redirect
    // (2026-10-29) names the assigned specialist it was meant for and why
    // not them (not_available | no_answer); the server checks the reason.
    async ({ userId, toUserId, warm = false, mode = null, redirectFrom = null, reason = null }) => {
      const res = await axios.post(
        `/onboarding-board/clients/${userId}/hot-seat`,
        {
          toUserId,
          ...(warm ? { warm: true } : {}),
          ...(mode ? { mode } : {}),
          ...(redirectFrom ? { redirectFrom, reason } : {}),
        },
      );
      return res.data;
    },
    {
      ...opts,
      onSuccess: (...a) => {
        queryClient.invalidateQueries(['client-onboarding']);
        // Signed today shows who took them.
        queryClient.invalidateQueries(['onboarding-potential']);
        opts.onSuccess?.(...a);
      },
    },
  );
};

// The agreement landed while a call was live (Nic 2026-10-05): only those
// get a hot seat and count toward the live hot seat rate.
export const useMarkSignedOnCall = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ userId }) => {
      const res = await axios.post(`/onboarding-board/clients/${userId}/signed-on-call`);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (...a) => {
        queryClient.invalidateQueries(['onboarding-potential']);
        opts.onSuccess?.(...a);
      },
    },
  );
};

// Nurture + people search on the Sales board (Nic 2026-09-28, moved off Lead
// Handoffs). Keyed under 'onboarding-board' so every board refresh (the live
// stream, a pull in) refreshes them too.
export const useNurtureSummary = (opts = {}) =>
  useQuery(
    ['onboarding-board', 'nurture-summary'],
    async () => (await axios.get('/onboarding-board/nurture/summary')).data,
    { refetchInterval: 60000, refetchIntervalInBackground: true, ...opts },
  );

export const useNurtureList = ({ page = 1, q = '', reason = '', mine = false } = {}, opts = {}) =>
  useQuery(
    ['onboarding-board', 'nurture', page, q, reason, mine],
    async () =>
      (
        await axios.get('/onboarding-board/nurture', {
          params: { page, q, reason: reason || undefined, mine: mine ? 1 : undefined },
        })
      ).data,
    { keepPreviousData: true, ...opts },
  );

export const usePullInNurture = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    // toUserId: the manager picks who gets them (Nic 2026-10-23).
    async ({ leadId, toUserId }) =>
      (await axios.post(`/onboarding-board/nurture/${leadId}/pull-in`, toUserId ? { toUserId } : {})).data,
    {
      onSuccess: () => queryClient.invalidateQueries(['onboarding-board']),
      ...opts,
    },
  );
};

// Answer a reach-out without re-engaging (Nic 2026-10-15): Leave in Nurture
// (as 'nurture') or Leave dropped (as 'dropped'), with an optional note.
export const useLeaveReachOut = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ leadId, as, note }) =>
      (
        await axios.post(`/onboarding-board/${as === 'dropped' ? 'dropped' : 'nurture'}/${leadId}/leave`, {
          note: note || undefined,
        })
      ).data,
    {
      onSuccess: () => queryClient.invalidateQueries(['onboarding-board']),
      ...opts,
    },
  );
};

// The Dropped tab (Nic 2026-10-15): server-paginated like Nurture.
export const useDroppedSummary = (opts = {}) =>
  useQuery(
    ['onboarding-board', 'dropped-summary'],
    async () => (await axios.get('/onboarding-board/dropped/summary')).data,
    { refetchInterval: 60000, refetchIntervalInBackground: true, ...opts },
  );

export const useDroppedList = ({ page = 1, q = '', reason = '', mine = false } = {}, opts = {}) =>
  useQuery(
    ['onboarding-board', 'dropped', page, q, reason, mine],
    async () =>
      (
        await axios.get('/onboarding-board/dropped', {
          params: { page, q, reason: reason || undefined, mine: mine ? 1 : undefined },
        })
      ).data,
    { keepPreviousData: true, ...opts },
  );

// Only after a reach-out; a stop-reason drop sends confirmStop (the UI
// shows the reason first).
export const useReengageDropped = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ leadId, confirmStop, toUserId }) =>
      (
        await axios.post(`/onboarding-board/dropped/${leadId}/reengage`, {
          confirmStop: confirmStop || undefined,
          toUserId: toUserId || undefined,
        })
      ).data,
    {
      onSuccess: () => queryClient.invalidateQueries(['onboarding-board']),
      ...opts,
    },
  );
};

export const usePeopleSearch = (q, opts = {}) =>
  useQuery(
    ['onboarding-people-search', q],
    async () =>
      (await axios.get('/onboarding-board/search', { params: { q, limit: 25 } })).data,
    { enabled: String(q || '').trim().length >= 2, keepPreviousData: true, ...opts },
  );

// Add a person (Nic 2026-10-20): where are they already, then add them
// straight onto Signing Up, or take a lead on file nobody is working.
export const usePersonCheck = (params, opts = {}) =>
  useQuery(
    ['onboarding-person-check', params],
    // POST, never a query string (QA 2026-10-26): name, phone and email
    // would land in URLs, logs and Sentry breadcrumbs. Read only.
    async () => (await axios.post('/onboarding-board/people/check', params)).data,
    // No keepPreviousData: a new number must never show the last one's
    // verdict while it loads (it says "Checking our system..." instead).
    { staleTime: 2000, ...opts },
  );

// The events a rep can pick for "An event" (2026-10-22).
export const useAddPersonEvents = (opts = {}) =>
  useQuery(['onboarding-person-events'], async () => (await axios.get('/onboarding-board/people/events')).data, {
    staleTime: 60000,
    ...opts,
  });

const afterPersonChange = (queryClient) => {
  queryClient.invalidateQueries(['onboarding-board']);
  queryClient.invalidateQueries(['onboarding-potential']);
  queryClient.invalidateQueries(['onboarding-people-search']);
  queryClient.invalidateQueries(['onboarding-person-check']);
};

export const useAddPerson = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload) => (await axios.post('/onboarding-board/people', payload)).data,
    { ...opts, onSuccess: (data, vars) => { afterPersonChange(queryClient); opts.onSuccess?.(data, vars); } },
  );
};

// Team settings (auto-nurture days), managers of either team.
export const useTeamSettings = (opts = {}) =>
  useQuery(
    ['team-settings'],
    async () => (await axios.get('/onboarding-board/team-settings')).data,
    { staleTime: 30000, ...opts },
  );

export const useSaveTeamSettings = (opts = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (patch) => (await axios.put('/onboarding-board/team-settings', patch)).data,
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['team-settings'], data);
        queryClient.invalidateQueries(['onboarding-board']);
        queryClient.invalidateQueries(['client-onboarding']);
      },
      ...opts,
    },
  );
};
